import { ChangeEvent, ChangeEventHandler, cloneElement, FocusEventHandler, UIEvent, useCallback, useEffect, useRef, useState } from "react";
import { usePPA } from "context/ppa-context";
import { Field, Input, Popover, PopoverProps, PopoverSurface, PopoverTrigger, makeStyles, shorthands, tokens } from "@fluentui/react-components";
import { IApiError, IPaginatedResult } from "@interfaces/common.interfaces";
import { getError } from "lib/utils";
import { useL10n } from "context/l10n-context";
import { ILocaleStrings } from "loc/locale.interfaces";
import { IObjectPickerInputProps } from "./object-picker-input.props";
import ObjectPickerResultsHeader from "./object-picker-results-header";
import { useDebounce } from "hooks/useDebounce";

const layoutStyles = makeStyles({
    mainWrapper: {
        position: 'relative'
    },
    inputWrapper: {
        ...shorthands.padding('0.5em'),
        ...shorthands.borderWidth('1px'),
        ...shorthands.borderStyle('none', 'none', 'solid', 'none'),
        ...shorthands.borderColor(tokens.colorNeutralStrokeSubtle),
    },
    input: {
        ...shorthands.borderStyle('none'),
        backgroundColor: 'transparent',
        ...shorthands.outline(0),
        width: '100%',
        height: '2em',
        fontFamily: 'var(--fontFamilyBase)',
        fontSize: 'var(--fontSizeBase300)'
    },
    resultsContainer: {
        width: '100%',
        overflowY: 'auto',
        maxHeight: '300px'
    }
});


export default function ObjectPickerInput<T>(props: IObjectPickerInputProps<T>) {
    const styles = layoutStyles();
    const { t } = useL10n<ILocaleStrings>();
    const { getApiClient } = usePPA();

    const [open, setOpen] = useState(false);
    const [allResults, setAllResults] = useState<Array<T> | null>(null); // null = waiting for cache
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [hasMore, setHasMore] = useState<boolean>(false);
    const [totalCount, setTotalCount] = useState<number>(0);

    const [searchString, setSearchString] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingMore, setLoadingMore] = useState<boolean>(false);

    const [hasFocus, setHasFocus] = useState<boolean>(false);
    const [apiError, setApiError] = useState<IApiError | null>(null);

    const resultsContainerRef = useRef<HTMLDivElement>(null);
    const lastOpenTimeRef = useRef<number>(0);
    const openTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Debounce search input (300ms)
    const debouncedSearchString = useDebounce(searchString, 300);

    // Page size is always 10
    const pageSize = 10;

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (openTimeoutRef.current) {
                clearTimeout(openTimeoutRef.current);
            }
        };
    }, []);

    // Reset pagination when search string changes
    useEffect(() => {
        if (searchString !== debouncedSearchString) {
            // User is still typing, don't reset yet
            return;
        }

        // Search string has stabilized, reset pagination
        setAllResults(null);
        setCurrentPage(1);
        setHasMore(false);
        setTotalCount(0);
        setApiError(null);
    }, [debouncedSearchString]);

    // Notify parent of results changes
    useEffect(() => {
        props.onResultsChange(allResults || []);
    }, [allResults]);

    // Fetch results (initial or next page)
    const fetchResults = useCallback(async (search: string, page: number, append: boolean = false) => {
        if (!search || search.length === 0) {
            setAllResults(null);
            setOpen(false);
            setHasMore(false);
            setTotalCount(0);
            return;
        }

        try {
            if (append) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }

            lastOpenTimeRef.current = Date.now();
            setOpen(true);
            setApiError(null);

            // Build URL with pagination parameters
            const url = props.getSearchUrl(search, page, pageSize);

            const response = await getApiClient().get<IPaginatedResult<T>>(url);

            const paginatedData = response.data;
            const items = paginatedData?.items || [];
            const newHasMore = paginatedData?.hasMore || false;
            const newTotalCount = paginatedData?.totalCount || 0;

            if (append) {
                setAllResults(prev => [...(prev || []), ...items]);
            } else {
                setAllResults(items);
            }

            setHasMore(newHasMore);
            setTotalCount(newTotalCount);

            // Keep popover open if we have results or we're loading
            if (items.length > 0 || append) {
                lastOpenTimeRef.current = Date.now();
                setOpen(true);
            }
        }
        catch (ex: unknown) {
            const error = getError(ex);
            console.log(`ObjectPickerInput -> fetchResults -> Error ->`, error);
            setApiError(error);
            setOpen(false);
        }
        finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [getApiClient, props.getSearchUrl, pageSize]);

    // Effect to trigger search when debounced value changes
    useEffect(() => {
        if (loading || loadingMore) {
            return; // Don't trigger new search while loading
        }

        if (debouncedSearchString.length > 0) {
            fetchResults(debouncedSearchString, 1, false);
        } else {
            setAllResults(null);
            setOpen(false);
            setHasMore(false);
            setTotalCount(0);
            setApiError(null);
        }
    }, [debouncedSearchString, fetchResults]);

    // Handle scroll for infinite scroll
    const handleScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
        if (!hasMore || loadingMore || loading) {
            return;
        }

        const target = event.currentTarget;
        const scrollThreshold = 50; // Load more when within 50px of bottom

        if (target.scrollHeight - target.scrollTop <= target.clientHeight + scrollThreshold) {
            const nextPage = currentPage + 1;
            setCurrentPage(nextPage);
            fetchResults(debouncedSearchString, nextPage, true);
        }
    }, [hasMore, loadingMore, loading, currentPage, debouncedSearchString, fetchResults]);

    const onSelect = (selection: T) => {
        console.log(`ObjectPickerInput -> onSelect -> `, selection);
        props.onSelect(selection);
        setOpen(false);
    }

    const onChange: ChangeEventHandler<HTMLInputElement> = (ev: ChangeEvent<HTMLInputElement>) => {
        console.log(`ObjectPickerInput -> onChange -> `, ev.target.value);
        props.onChange(ev.target.value);
        setSearchString(ev.target.value);
    }

    const handleOpenChange: PopoverProps["onOpenChange"] = (e, data) => {
        console.log(`ObjectPickerInput -> handleOpenChange -> `, e, data);

        // Prevent closing immediately after opening (within 300ms)
        const timeSinceOpen = Date.now() - lastOpenTimeRef.current;
        if (!data.open && timeSinceOpen < 300) {
            console.log(`ObjectPickerInput -> Prevented rapid close (${timeSinceOpen}ms since open)`);
            return;
        }

        // Only allow closing if input doesn't have focus
        if (!data.open && !hasFocus) {
            setOpen(false);
        }
    }

    const onFocus: FocusEventHandler<HTMLInputElement> = (ev: any) => {
        console.log(`ObjectPickerInput -> onFocus -> `, ev);
        setHasFocus(true);

        // Clear any pending close timeout
        if (openTimeoutRef.current) {
            clearTimeout(openTimeoutRef.current);
            openTimeoutRef.current = null;
        }

        // Open popover if there's content to show
        if (ev?.target?.value && allResults && allResults.length > 0) {
            // Use a small delay to ensure focus has settled
            openTimeoutRef.current = setTimeout(() => {
                lastOpenTimeRef.current = Date.now();
                setOpen(true);
            }, 50);
        }
    }

    const onBlur: FocusEventHandler<HTMLInputElement> = (ev: any) => {
        console.log(`ObjectPickerInput -> onBlur -> `, ev);

        // Check if we're blurring to an element inside the popover
        const relatedTarget = ev.relatedTarget as HTMLElement;
        if (relatedTarget && resultsContainerRef.current?.contains(relatedTarget)) {
            // Don't lose focus if clicking inside the popover
            return;
        }

        setHasFocus(false);

        // Clear any pending open timeout
        if (openTimeoutRef.current) {
            clearTimeout(openTimeoutRef.current);
            openTimeoutRef.current = null;
        }
    }

    return (
        <>
            {props.visible && <div className={styles.mainWrapper}>
                <Field validationMessage={apiError?.message}>
                    <Input
                        id={props.inputId}
                        onChange={props.disabled ? undefined : onChange}
                        onFocus={props.disabled ? undefined : onFocus}
                        onBlur={props.disabled ? undefined : onBlur}
                        autoComplete="off"
                        placeholder={t("pickerFilterPlaceholder")}
                        appearance="underline"
                        disabled={props.disabled} />
                </Field>
                {!props.disabled &&
                    <Popover inline open={open} onOpenChange={handleOpenChange} size="large" positioning={"below-start"} >
                        <PopoverTrigger disableButtonEnhancement>
                            <span></span>
                        </PopoverTrigger>
                        <PopoverSurface>
                            <ObjectPickerResultsHeader
                                loading={loading}
                                searchString={debouncedSearchString}
                                results={allResults}
                                totalCount={totalCount}
                                showingCount={allResults?.length || 0}
                                hasMore={hasMore} />
                            <div
                                ref={resultsContainerRef}
                                className={styles.resultsContainer}
                                onScroll={handleScroll}>
                                {allResults?.map((object: T, index: number) => (
                                    <div key={index}>
                                        {cloneElement(props.objectCard, { item: object, onSelect: onSelect, style: "subtle" })}
                                    </div>
                                ))}
                                {loadingMore && (
                                    <div style={{ padding: '1em', textAlign: 'center' }}>
                                        {t("loading")}...
                                    </div>
                                )}
                            </div>
                        </PopoverSurface>
                    </Popover>}
            </div>
            }
        </>
    );
}